import { IS_TEMPLATE_PINNING_ENABLED } from '@/app-shell/flags/templates-flags';
import { countMarkdownSections } from '@/helpers/sectionParser';
import { GET_DOCUMENT_TEMPLATES } from '@/hooks/query/useQueryDocumentTemplates';
import { GET_TEMPLATE_BY_ID } from '@/hooks/query/useQueryTemplateById';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  GET_INITIAL_PINNED_TEMPLATES,
  GET_PINNED_TEMPLATES,
  type TemplateResponse,
} from '@heidi/templates/contracts';

/**
 * Helper function to check if a template is a starter pack template.
 * Returns false when templateId is falsy,
 * otherwise reads from reactQueryClient cache and checks if template is in the initial pinned templates list.
 *
 * @param templateId - The ID of the template to check
 * @returns true if the template is a starter pack template, false otherwise
 */
/**
 * Helper function to check if a template is pinned.
 * Returns false when the pinning flag is disabled or templateId is falsy,
 * otherwise reads from reactQueryClient cache and checks if template is in the pinned templates list.
 *
 * @param templateId - The ID of the template to check
 * @returns true if the template is pinned, false otherwise
 */
export const getIsPinnedTemplateFromCache = (templateId?: string | null): boolean => {
  if (!IS_TEMPLATE_PINNING_ENABLED()) return false;
  if (!templateId) return false;

  const pinnedTemplates = reactQueryClient.getQueryData<TemplateResponse[]>([
    GET_PINNED_TEMPLATES,
  ]);
  return pinnedTemplates?.some((t: TemplateResponse) => t.id === templateId) ?? false;
};

export const getIsStarterTemplateFromCache = (templateId?: string | null): boolean => {
  if (!templateId) {
    return false;
  }
  const starterTemplates = reactQueryClient.getQueryData<TemplateResponse[]>([
    GET_INITIAL_PINNED_TEMPLATES,
  ]);
  return starterTemplates?.some((t: TemplateResponse) => t.id === templateId) ?? false;
};

export const getTemplateNameFromCache = (
  templateId?: string | null
): string | undefined => {
  if (!templateId) return undefined;

  const pinnedTemplates = reactQueryClient.getQueryData<TemplateResponse[]>([
    GET_PINNED_TEMPLATES,
  ]);
  const match = pinnedTemplates?.find((t: TemplateResponse) => t.id === templateId);
  if (match) return match.name;

  const starterTemplates = reactQueryClient.getQueryData<TemplateResponse[]>([
    GET_INITIAL_PINNED_TEMPLATES,
  ]);
  return starterTemplates?.find((t: TemplateResponse) => t.id === templateId)?.name;
};

/**
 * Best-effort check of whether the template behind a generated note is
 * "sectional" — its `structure_template` opens at least one `# ` section.
 * Reads only the TanStack Query cache (used for the `is_sectional` dimension on
 * `note_created`), scanning the per-id cache across `generationMethod` variants
 * then the document-templates list. A cache miss defaults to `false`.
 */
export const getIsSectionalTemplateFromCache = (templateId?: string | null): boolean => {
  if (!templateId) return false;

  // GET_TEMPLATE_BY_ID key = [GET_TEMPLATE_BY_ID, { templateId }, generationMethod].
  const byIdEntries = reactQueryClient.getQueriesData<TemplateResponse>({
    queryKey: [GET_TEMPLATE_BY_ID],
  });
  for (const [key, template] of byIdEntries) {
    const keyTemplateId = (key[1] as { templateId?: string } | undefined)?.templateId;
    if (keyTemplateId === templateId && template) {
      return countMarkdownSections(template.structure_template) > 0;
    }
  }

  const documentTemplates = reactQueryClient.getQueryData<TemplateResponse[]>([
    GET_DOCUMENT_TEMPLATES,
  ]);
  const match = documentTemplates?.find((t: TemplateResponse) => t.id === templateId);
  return match ? countMarkdownSections(match.structure_template) > 0 : false;
};
